<template lang="pug">
.business-card-form(ref="formContainer")
    .business-card-form-grid
        .col.left
            el-form-item(:label="$t('Name')" :prop="propPath ? `${propPath}.name` : 'name'")
                el-input(
                    v-model="nameModel"
                    :placeholder="cmsName"
                    maxlength="255"
                    show-word-limit
                )

            el-form-item.description-form-item(
                ref="descriptionFormItem"
                :label="$t('Description')"
                :prop="propPath ? `${propPath}.description` : 'description'"
                :show-message="false"
            )
                .description-editor(:class="{ 'has-error': !!descriptionErrorMessage }")
                    quill-editor(
                        v-if="quillConfig.bounds"
                        ref="quill"
                        v-model="descriptionModel"
                        :options="quillConfig"
                    )
                    .description-meta
                        .error-text(v-if="descriptionErrorMessage") {{ descriptionErrorMessage }}
                        .char-counter(:class="{ exceeded: descriptionCharCount > 300 }") {{ descriptionCharCount }}/300

            .location-section
                label.el-form-item__label {{ $t('Location') }}
                .address-card-wrapper(v-if="hasOverrideAddress")
                    el-button.remove-btn(
                        plain
                        circle
                        size="mini"
                        type="danger"
                        icon="el-icon-fa-trash-alt fas"
                        @click="$emit('remove-address')"
                    )
                    AddressCardInput(
                        :form="value"
                        formPath="address"
                        :propPath="propPath ? `${propPath}.address` : 'address'"
                        :fields="['street', 'zipcode', 'city', 'country']"
                        @field-change="onAddressFieldChange"
                    )
                .address-fallback(v-else)
                    slot(name="address-fallback")

            el-form-item(:label="$t('Category')" :prop="propPath ? `${propPath}.category` : 'category'")
                el-select.is-full-width(
                    v-model="categoryModel"
                    value-key="id"
                    clearable
                )
                    el-option(
                        v-for="cat in categoryOptions"
                        :key="cat.id"
                        :label="cat.name"
                        :value="cat"
                    )
                        span
                            i.category-icon(v-if="cat.icon" :class="cat.icon")
                            | {{ cat.name }}

            el-form-item(:label="$t('Meta Tags')")
                el-select.is-full-width(
                    :value="metaTagValues"
                    multiple
                    filterable
                    allow-create
                    default-first-option
                    @change="onMetaTagsChange"
                )
                    el-option(
                        v-for="tag in tagOptions"
                        :key="tag.id"
                        :label="tag.name"
                        :value="tag.name"
                    )

            el-form-item(:label="$t('Homepage URL')" :prop="propPath ? `${propPath}.homepage_url` : 'homepage_url'")
                el-input(
                    v-model="homepageUrlModel"
                    type="url"
                )

            .flex-row
                el-form-item.grow(:label="$t('Phone')" :prop="propPath ? `${propPath}.phone` : 'phone'")
                    el-input(
                        v-model="phoneModel"
                        type="tel"
                    )
                el-form-item.grow(:label="$t('Email')" :prop="propPath ? `${propPath}.email` : 'email'")
                    el-input(
                        v-model="emailModel"
                        type="email"
                    )

        .col.right
            el-form-item(:label="$t('Business Image')")
                .business-card-image-preview(v-if="businessCardImage")
                    a.image-link(
                        href="#"
                        @click.prevent="openImageCropper"
                    )
                        img.cropped-preview(:src="businessCardImageCroppedSrc")
                        .edit-popover
                            i.el-icon-fa-pencil-alt.fas
                    el-button.delete-image(
                        circle
                        type="info"
                        size="mini"
                        icon="el-icon-fa-trash-alt far"
                        @click="removeImage"
                    )
                el-upload.plus-box(
                    v-else
                    ref="upload"
                    drag
                    :action="uploadEndpoint"
                    accept="image/jpg, image/jpeg, image/png"
                    :before-upload="validateUpload"
                    :on-change="updateUploadHeaders"
                    :on-success="onUploadSuccess"
                    :on-error="onUploadError"
                    :headers="uploadHeaders"
                    :data="uploadData"
                )
                    i.el-icon-plus
                    .el-upload__text(v-html="$t('upload.drop.plural')")
                    .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png', size: '3mb' }) }}
                el-button.mt-2(
                    v-if="!businessCardImage"
                    plain
                    size="small"
                    icon="el-icon-picture-outline"
                    @click="showLibraryDialog = true"
                ) {{ $t('Select from library') }}

            el-form-item(:label="$t('Opening Hours')")
                OpeningHoursEditor(
                    :value="value.opening_hours"
                    @input="onOpeningHoursChange"
                )

            .flex-row
                el-switch(
                    :active-text="$t('Can visit location')"
                    v-model="canVisitLocationModel"
                )
                el-switch(
                    :active-text="$t('Delivery service')"
                    v-model="hasDeliveryServiceModel"
                )

    //- Unified crop + AI + variant editor (1:1 logo, presets hidden). Replaces the
    //- legacy ImageCropperModal. Crop/AI persist server-side; @updated syncs the
    //- result back so the form save doesn't clobber the active variant.
    generate-image-edit-drawer(
        v-if="businessCardImage"
        :visible.sync="imageEditorVisible"
        :image="businessCardImage"
        :fixed-aspect-ratio="1"
        :mutable="isBusinessCardImagePersisted"
        :upload-context="imageEditorUploadContext"
        @updated="onBusinessCardImageUpdated"
        @replaced="onBusinessCardImageReplaced"
    )

    image-library-dialog(
        :visible.sync="showLibraryDialog"
        :multiple="false"
        selection-type="image"
        @select="onBusinessCardLibrarySelected"
    )
</template>

<i18n>
{
    "de": {
        "Location": "Standort",
        "Name": "Name",
        "Description": "Beschreibung",
        "Category": "Kategorie",
        "Meta Tags": "Meta-Tags",
        "Homepage URL": "Webseite",
        "Phone": "Telefon",
        "Email": "E-Mail",
        "Business Image": "Geschäftsbild",
        "Select from library": "Aus Mediathek wählen",
        "Opening Hours": "Öffnungszeiten",
        "Can visit location": "Besuchbar vor Ort",
        "Delivery service": "Lieferservice"
    }
}
</i18n>

<script>
import Vue from 'vue'
import _ from 'lodash'
import striptags from 'striptags'

import { ensureFreshToken, getFitStringFromProcessObject } from '@/helpers'
import { useAuthStore, useSetupStore } from '@/stores'

import AddressCardInput from '@/components/AddressCardInput'
import OpeningHoursEditor from '@/components/OpeningHoursEditor'
import GenerateImageEditDrawer from '@/extensions/generative-media/views/Drawers/GenerateImageEditDrawer'
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog'

export default {
    name: 'BusinessCardForm',
    components: {
        AddressCardInput,
        OpeningHoursEditor,
        GenerateImageEditDrawer,
        ImageLibraryDialog,
    },
    props: {
        // The BusinessCard object the form binds to. The component mutates
        // its scalar fields directly (description, name, image, etc.) — same
        // contract `AddressCardInput` already uses for `:form`.
        value: {
            type: Object,
            required: true,
        },
        // The CMS that owns this card — used for the upload endpoint and as
        // the placeholder for the BC name field.
        cms: {
            type: Object,
            required: true,
        },
        // Form-item rule path prefix, e.g. "businessCard" or "businessCards.0".
        // Each form-item builds its full prop as `${propPath}.field`. Pass an
        // empty string when the parent el-form binds directly to this card
        // (the drawer flow) so the form-items use bare field names.
        propPath: {
            type: String,
            default: '',
        },
        categoryOptions: {
            type: Array,
            default: () => [],
        },
        tagOptions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            imageEditorVisible: false,
            showLibraryDialog: false,
            descriptionErrorMessage: '',
            quillConfig: {
                theme: 'snow',
                placeholder: '',
                bounds: undefined,
                modules: {
                    imageBlocker: true,
                    clipboard: { matchVisual: false },
                    toolbar: { container: [['bold']] },
                },
            },
        }
    },
    mounted() {
        this.$nextTick(() => {
            if (this.$refs.formContainer) {
                this.quillConfig.bounds = this.$refs.formContainer
            }
            // Mirror the el-form-item's validateMessage into a local data property
            // so we can render the error inline next to the character counter
            // (with `:show-message="false"` the default absolutely-positioned
            // error placement is suppressed).
            const formItem = this.$refs.descriptionFormItem
            if (formItem) {
                this.$watch(
                    () => formItem.validateMessage,
                    (message) => { this.descriptionErrorMessage = message || '' },
                    { immediate: true }
                )
            }
        })
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        cmsName() {
            return _.get(this.cms, 'name', '') || ''
        },
        nameModel: {
            get() {
                return _.get(this.value, 'name', '') || ''
            },
            set(val) {
                Vue.set(this.value, 'name', val)
            },
        },
        descriptionModel: {
            get() {
                return _.get(this.value, 'description', '') || ''
            },
            set(val) {
                Vue.set(this.value, 'description', val)
                // vee-element auto-wires change/blur validation listeners on
                // Element UI inputs by listening to the `el.form.change` event
                // they bubble. The third-party quill-editor doesn't emit that
                // event, so the form-item's validation never re-runs on input.
                // Nudge it manually whenever the model changes so the error
                // clears (and re-triggers) live, matching regular fields.
                this.$nextTick(() => {
                    const formItem = this.$refs.descriptionFormItem
                    if (formItem && typeof formItem.validate === 'function') {
                        formItem.validate('change')
                    }
                })
            },
        },
        descriptionCharCount() {
            return striptags(this.descriptionModel || '').length
        },
        categoryModel: {
            get() {
                return _.get(this.value, 'category') || null
            },
            set(val) {
                Vue.set(this.value, 'category', val || null)
            },
        },
        homepageUrlModel: {
            get() {
                return _.get(this.value, 'homepage_url', '') || ''
            },
            set(val) {
                Vue.set(this.value, 'homepage_url', val)
            },
        },
        phoneModel: {
            get() {
                return _.get(this.value, 'phone', '') || ''
            },
            set(val) {
                Vue.set(this.value, 'phone', val)
            },
        },
        emailModel: {
            get() {
                return _.get(this.value, 'email', '') || ''
            },
            set(val) {
                Vue.set(this.value, 'email', val)
            },
        },
        canVisitLocationModel: {
            get() {
                return !!_.get(this.value, 'can_visit_location', false)
            },
            set(val) {
                Vue.set(this.value, 'can_visit_location', !!val)
            },
        },
        hasDeliveryServiceModel: {
            get() {
                return !!_.get(this.value, 'has_delivery_service', false)
            },
            set(val) {
                Vue.set(this.value, 'has_delivery_service', !!val)
            },
        },
        metaTagValues() {
            const tags = _.get(this.value, 'meta_tags', []) || []
            return tags.map((tag) => tag.name)
        },
        hasOverrideAddress() {
            const address = _.get(this.value, 'address')
            if (!address || address.deleted) return false
            // An override is "active" when the address either holds content,
            // has a persisted id (came back from the server), or was just
            // initialized via the parent's "add address" click (which seeds
            // a `new:xxx` id). The empty-default object on a fresh form has
            // none of these, so the fallback panel renders by default.
            const hasContent = !!(address.street || address.city || address.zipcode || address.country)
            const hasPersistedId = address.id && String(address.id).indexOf('new:') !== 0
            const wasInitialized = address.id && String(address.id).indexOf('new:') === 0
            return hasContent || hasPersistedId || wasInitialized
        },
        businessCardImage() {
            const image = _.get(this.value, 'image')
            if (!image || image.deleted) return null
            return image
        },
        // Variant/AI editing needs a persisted Image (uploaded logos have a real id).
        isBusinessCardImagePersisted() {
            const id = _.get(this.businessCardImage, 'id')
            return !!id && String(id).indexOf('new:') !== 0
        },
        uploadEndpoint() {
            const base = useSetupStore().uploadEndpoint
            return this.cms && this.cms.id ? `${base}/public/${this.cms.id}/` : base
        },
        uploadData() {
            const data = { 'context[type]': 'business-card-image' }
            if (this.cms && this.cms.id) {
                data['context[model]'] = 'CityPages\\Cms'
                data['context[id]'] = this.cms.id
            }
            return data
        },
        // Lets the image editor's own Replace/Upload control post to the business-card
        // upload endpoint with the right morph context.
        imageEditorUploadContext() {
            const context = { endpoint: this.uploadEndpoint, type: 'business-card-image' }
            if (this.cms && this.cms.id) {
                context.model = 'CityPages\\Cms'
                context.id = this.cms.id
            }
            return context
        },
        businessCardImageCroppedSrc() {
            if (!this.businessCardImage) return ''
            const image = this.businessCardImage
            // A unified-editor crop/AI edit bakes the final pixels into the active
            // variant — show it directly (no re-crop).
            const variantUrl = _.get(image, 'processedFile.url')
            if (variantUrl) {
                return variantUrl
            }
            const publicStorageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`, '/')
            const table = image.upload ? 'uploads' : 'images'
            let fit = 'fit=crop&crop=focalpoint&w=150&h=150&x=.5&y=.5&z=1'
            const processing = _.get(image, 'settings.processing')
            if (processing) {
                fit = getFitStringFromProcessObject(processing)
            }
            return `${publicStorageRoute}/${table}/${image.id}/${image.name}?${fit}`
        },
        originalImageSrc() {
            if (!this.businessCardImage) return ''
            const image = this.businessCardImage
            const publicStorageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`, '/')
            const table = image.upload ? 'uploads' : 'images'
            return `${publicStorageRoute}/${table}/${image.id}/${image.name}`
        },
    },
    methods: {
        onAddressFieldChange({ field, value }) {
            const address = _.get(this.value, 'address')
            if (!address) return
            Vue.set(address, field, value)
        },
        onOpeningHoursChange(value) {
            Vue.set(this.value, 'opening_hours', value)
        },
        onMetaTagsChange(values) {
            const tags = values.map((tagName, index) => {
                const existing = (this.tagOptions || []).find((tag) => tag.name === tagName)
                if (existing) {
                    return { id: existing.id, key: existing.key, name: existing.name }
                }
                return { id: `new:${index}`, name: tagName }
            })
            Vue.set(this.value, 'meta_tags', tags)
        },

        // ---- Image upload + cropper ----
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken()
        },
        async validateUpload(file) {
            await this.updateUploadHeaders()
            if (file.size / 1024 / 1024 > 3) {
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', { size: '3mb' }),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                // Reject, never `return false`: an async before-upload always returns a promise,
                // and Element aborts only on rejection - a resolved `false` uploads anyway.
                return Promise.reject()
            }
        },
        onUploadSuccess(response) {
            const image = response
            image.upload = true
            image.type = 'business-card-image'
            image.width = _.get(response, 'dimensions.width')
            image.height = _.get(response, 'dimensions.height')
            image.title = image.title || ''
            image.alt = image.alt || ''
            if (typeof image.size === 'number') {
                image.size = image.size.toString()
            }
            delete image.hash
            delete image.dimensions
            Vue.set(this.value, 'image', image)
        },
        onUploadError() {
            this.$notify.error({
                title: this.$t('Upload failed'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 4000,
            })
        },
        removeImage() {
            const image = _.get(this.value, 'image')
            if (image && image.id) {
                Vue.set(image, 'deleted', true)
            } else {
                Vue.set(this.value, 'image', null)
            }
        },
        openImageCropper() {
            if (!this.businessCardImage) return
            this.imageEditorVisible = true
        },
        // The unified editor persists crop/AI server-side and emits the fresh image.
        // Sync it into value.image so the form save sends current state (cleared
        // settings.processing + active variant pointer), never clobbering the variant.
        onBusinessCardImageUpdated(updatedImage) {
            if (!updatedImage) return
            const keys = ['processing_query_string', 'processedFile', 'activeVariant', 'variants', 'settings', 'source', 'width', 'height']
            const image = _.get(this.value, 'image')
            if (!image) {
                Vue.set(this.value, 'image', updatedImage)
                return
            }
            keys.forEach(key => Vue.set(image, key, _.get(updatedImage, key)))
        },
        // The editor replaced the image (uploaded a new file or picked one from the
        // library) — swap the whole image so the card save persists the new source.
        onBusinessCardImageReplaced(image) {
            if (!image) return
            Vue.set(this.value, 'image', image)
        },
        // "Select from library" on the form (the upload alternative): set the chosen
        // library image as the card image. The backend's library-selection branch
        // (SaveCms::applyBusinessCardInput) attaches the File on save.
        onBusinessCardLibrarySelected(selection) {
            const image = Array.isArray(selection) ? selection[0] : selection
            if (image) Vue.set(this.value, 'image', { ...image, type: 'business-card-image' })
        },
    },
}
</script>

<style lang="sass" scoped>
.business-card-form
    container-type: inline-size

.business-card-form-grid
    display: grid
    grid-template-columns: 1fr
    gap: 20px

@container (min-width: 720px)
    .business-card-form-grid
        grid-template-columns: 1fr 1fr

.flex-row
    display: flex
    gap: 12px
    > .grow
        flex: 1

.location-section
    margin-bottom: 22px
    .el-form-item__label
        display: block
        font-size: 14px
        color: #606266
        line-height: 1.3
        padding-bottom: 5px

.address-card-wrapper
    position: relative
    .remove-btn
        position: absolute
        top: -8px
        right: -8px
        z-index: 10

.description-editor
    position: relative
    ::v-deep .quill-editor
        .ql-toolbar
            border-radius: 4px 4px 0 0
            border-color: #dcdfe6
        .ql-container
            border-radius: 0 0 4px 4px
            border-color: #dcdfe6
            min-height: 80px
        .ql-editor
            min-height: 80px
    // Project's global _quill.sass paints the visible border on .ql-editor
    // (the toolbar carries its own top/left/right border, the .ql-container
    // wrapper is intentionally borderless). The :focus rule there swaps the
    // editor's border to blue, so we must override that too — otherwise
    // focusing an invalid editor flips the bottom half back to blue.
    &.has-error
        ::v-deep
            .ql-toolbar.ql-snow
                border-color: #f56c6c
            .ql-editor,
            .ql-editor:focus
                border-color: #f56c6c

.description-meta
    display: flex
    align-items: baseline
    gap: 12px
    line-height: 1.4
    .error-text
        flex: 1
        font-size: 12px
        color: #f56c6c
    .char-counter
        margin-left: auto
        font-size: 12px
        color: #909399
        &.exceeded
            color: #f56c6c

.business-card-image-preview
    position: relative
    display: inline-block
    .image-link
        display: block
        position: relative
        cursor: pointer
        border-radius: 4px
        overflow: hidden
        &:hover .edit-popover
            opacity: 1
    .cropped-preview
        display: block
        width: 150px
        height: 150px
        border-radius: 4px
        border: 1px solid #dcdfe6
        object-fit: cover
    .edit-popover
        position: absolute
        top: 0
        left: 0
        right: 0
        bottom: 0
        background: rgba(0, 0, 0, 0.5)
        display: flex
        align-items: center
        justify-content: center
        opacity: 0
        transition: opacity 0.15s ease
        border-radius: 4px
        i
            color: #fff
            font-size: 24px
    .delete-image
        position: absolute
        top: -10px
        right: -10px
        z-index: 10
        width: 30px
        height: 30px

.plus-box
    ::v-deep .el-upload
        width: 100%
    ::v-deep .el-upload-dragger
        width: 100%
        height: 150px
        display: flex
        flex-direction: column
        align-items: center
        justify-content: center
    .el-icon-plus
        font-size: 40px
        color: #c0c4cc
    .el-upload__text
        margin-top: 10px
        font-size: 12px
        color: #606266
    .el-upload__tip
        font-size: 11px
        color: #909399
        margin-top: 5px

.category-icon
    margin-right: 8px
</style>
